
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import { useTenants } from '../hooks/useTenants';
import { Tenant } from '../types';
import toast from 'react-hot-toast';
import { ProfileModal } from './ProfileModal';

// Icons
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  Megaphone,
  Settings,
  ShieldCheck,
  LogOut,
  User,
  CreditCard,
  ChevronRight,
  ChevronLeft,
  ChevronsUpDown,
  Building,
  Check
} from 'lucide-react';

export function Navigation() {
  const location = useLocation();
  const { settings, loading: settingsLoading } = useSettings();
  const { user, logout } = useAuth();
  const { tenants: userTenants, currentTenant, switchTenant, loading: tenantLoading } = useTenants();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTenantMenuOpen, setIsTenantMenuOpen] = useState(false);
  const [allTenants, setAllTenants] = useState<Tenant[]>([]);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // Load all tenants for SUPERADMIN to allow switching to any tenant
  useEffect(() => {
    if (isSuperAdmin && isTenantMenuOpen) {
      const fetchAllTenants = async () => {
        try {
          const token = localStorage.getItem('auth_token');
          const response = await fetch('/api/tenants', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setAllTenants(data.tenants || []);
          }
        } catch (error) {
          console.error('Error fetching all tenants:', error);
        }
      };
      fetchAllTenants();
    }
  }, [isSuperAdmin, isTenantMenuOpen]);

  const displayTenants = isSuperAdmin ? (allTenants.length > 0 ? allTenants : userTenants) : userTenants;

  // Refs for click outside handling
  const userMenuRef = useRef<HTMLDivElement>(null);
  const tenantMenuRef = useRef<HTMLDivElement>(null);

  // Update CSS variable for sidebar width
  useEffect(() => {
    const width = isExpanded ? '197px' : '58px'; // 58px for collapsed state
    document.documentElement.style.setProperty('--sidebar-width', width);
  }, [isExpanded]);

  // Click outside handlers
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (tenantMenuRef.current && !tenantMenuRef.current.contains(event.target as Node)) {
        setIsTenantMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTenantSwitch = async (tenantId: string) => {
    setIsTenantMenuOpen(false);
    await switchTenant(tenantId);
  };

  const menuItems = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard size={24} />
    },
    {
      path: '/whatsapp',
      label: 'Conexões',
      icon: <MessageSquare size={24} /> // Or another relevant icon
    },
    {
      path: '/contatos',
      label: 'Contatos',
      icon: <Users size={24} />
    },
    {
      path: '/campanhas',
      label: 'Campanhas',
      icon: <Megaphone size={24} />
    },
    {
      path: '/campanhas/interativa',
      label: 'Interativas',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      ) // Custom layer icon or relevant one
    },
    // Users menu removed by request
    // ...(['ADMIN', 'TENANT_ADMIN', 'SUPERADMIN'].includes(user?.role || '') ? [{
    //   path: '/usuarios',
    //   label: 'Usuários',
    //   icon: <Users size={24} /> // Reuse users or specific icon
    // }] : []),
    {
      path: '/configuracoes',
      label: 'Configurações',
      icon: <Settings size={24} />
    }
  ];

  return (
    <nav
      className={`sidebar-navigation glass-sidebar flex flex-col transition-all duration-300 ${isExpanded ? 'w-[197px]' : 'w-[58px]'
        } flex flex-col justify-between rounded-r-2xl`}
      onMouseEnter={() => { }} // Disabled hover expansion
      onMouseLeave={() => { }} // Disabled hover expansion
    >
      {/* Toggle Button - Outside Sidebar */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute -right-3 top-12 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-blue-600 z-50 border border-gray-100 transition-transform duration-200 hover:scale-110"
        title={isExpanded ? "Recolher" : "Expandir"}
      >
        {isExpanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
      </button>

      {/* Top Section: Toggle & Tenant */}
      <div className="flex flex-col w-full pl-2 pr-4 py-4 space-y-6">

        {/* Header / Tenant Selector */}
        <div className="relative" ref={tenantMenuRef}>
          <button
            onClick={() => setIsTenantMenuOpen(!isTenantMenuOpen)}
            className={`w-full flex items-center ${isExpanded ? 'justify-between px-3 py-2' : 'justify-center'} 
              rounded-xl hover:bg-white/10 transition-colors duration-200 group`}
          >
            <div className="flex items-center gap-3">
              {/* Logo / Tenant Icon */}
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 backdrop-blur-sm border border-white/10">
                {settingsLoading ? (
                  <div className="w-6 h-6 animate-pulse bg-white/20 rounded" />
                ) : currentTenant?.companyProfile?.logoUrl ? (
                  <img src={currentTenant.companyProfile.logoUrl} alt="Logo" className="w-8 h-8 object-contain rounded" />
                ) : settings?.iconUrl ? (
                  <img src={settings.iconUrl} alt="Logo" className="w-6 h-6 object-contain" />
                ) : (
                  <Building size={20} className="text-white" />
                )}
              </div>

              {/* Tenant Name (Expanded Only) */}
              {isExpanded && (
                <div className="flex flex-col items-start overflow-hidden">
                  <span className="text-white font-light text-sm truncate w-32 text-left font-oswald uppercase tracking-wider">
                    {currentTenant?.name || settings?.companyName || 'Tenant'}
                  </span>
                  <span className="text-white/60 text-xs truncate font-light">
                    {currentTenant?.plan?.name || 'Plano Básico'}
                  </span>
                </div>
              )}
            </div>

            {/* Dropdown Chevron (Expanded Only) */}
            {isExpanded && (
              <ChevronsUpDown size={16} className="text-white/60 group-hover:text-white transition-colors" />
            )}
          </button>

          {/* Tenant Dropdown Menu */}
          {isTenantMenuOpen && (
            <div className={`
              absolute z-[9999] rounded-xl glass-dropdown p-2 shadow-xl animate-in fade-in 
              ${isExpanded
                ? 'top-full left-0 w-full mt-2 slide-in-from-top-2'
                : 'left-full ml-4 w-64 top-0 slide-in-from-left-2'
              }
            `}>
              <div className="text-xs font-semibold text-gray-500 uppercase px-3 py-2 mb-1">
                Alternar Empresa
              </div>
              <div className="space-y-1 max-h-60 overflow-y-auto custom-scrollbar">
                {displayTenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => handleTenantSwitch(tenant.id)}
                    disabled={tenantLoading}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors
                      ${(tenant.current || tenant.id === currentTenant?.id)
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <span className="truncate">{tenant.name}</span>
                    {(tenant.current || tenant.id === currentTenant?.id) && <Check size={14} className="text-blue-600" />}
                  </button>
                ))}
              </div>

            </div>
          )}
        </div>

        {/* Navigation Menu */}
        <div className="flex flex-col space-y-2 mt-4">
          {menuItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  relative flex items-center pl-2 pr-4 py-3 rounded-xl transition-all duration-200 group
                  ${isActive
                    ? 'bg-white/20 text-white shadow-lg backdrop-blur-md'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }
                  ${!isExpanded ? 'justify-center' : ''}
                `}
                title={!isExpanded ? item.label : undefined}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 ${isActive ? 'text-white' : 'text-white/80 group-hover:text-white'}`}>
                  {item.icon}
                </div>

                {/* Label (Expanded Only) */}
                {isExpanded && (
                  <span className="ml-3 font-light text-sm whitespace-nowrap overflow-hidden transition-all duration-300 opacity-100 font-oswald uppercase tracking-wider">
                    {item.label}
                  </span>
                )}

                {/* Active Indicator (Left Strip) */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white/80 rounded-r-full shadow-[0_0_8px_rgba(255,255,255,0.5)]"></div>
                )}

                {/* Tooltip for Collapsed State */}
                {!isExpanded && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-4 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50">
                    {item.label}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Bottom Section: User Profile */}
      <div className="p-2 border-t border-white/10 bg-black/10 backdrop-blur-sm" ref={userMenuRef}>
        <div className="relative">
          {/* User Menu Trigger */}
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className={`w-full flex items-center ${isExpanded ? 'pl-1 pr-3 py-2' : 'justify-center py-2'} 
              rounded-xl hover:bg-white/10 transition-colors duration-200 group`}
          >
            {/* Avatar */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 p-[2px] flex-shrink-0">
              <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center overflow-hidden">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="User" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-sm">
                    {user?.nome?.charAt(0)?.toUpperCase()}
                  </span>
                )}
              </div>
            </div>

            {/* User Info (Expanded Only) */}
            {isExpanded && (
              <div className="ml-3 flex-1 overflow-hidden text-left">
                <p className="text-sm font-light text-white truncate">{user?.nome}</p>
                <p className="text-xs text-white/60 truncate font-light">{user?.email}</p>
              </div>
            )}

            {/* Edit Icon / Settings (Expanded Only) - Optional hint */}
            {isExpanded && (
              <Settings size={14} className="text-white/40 group-hover:text-white/80 transition-colors ml-2" />
            )}

          </button>

          {/* User Context Menu */}
          {isUserMenuOpen && (
            <div className={`absolute bottom-full ${isExpanded ? 'left-0 w-full' : 'left-full ml-2 w-48'} mb-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 p-2 shadow-xl z-[2100] animate-in fade-in slide-in-from-bottom-2`}>
              <div className="space-y-1">
                <button
                  onClick={() => { setIsUserMenuOpen(false); setIsProfileModalOpen(true); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <User size={16} className="text-white/70" />
                  Meu Perfil
                </button>
                <button
                  onClick={() => {/* ToDo: Show Plan */ setIsUserMenuOpen(false); toast('Funcionalidade em desenvolvimento', { icon: '🚧' }) }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <CreditCard size={16} className="text-white/70" />
                  Meu Plano
                </button>

                {/* Super Admin Options */}
                {user?.role === 'SUPERADMIN' && (
                  <>
                    <div className="h-px bg-white/10 my-1"></div>
                    <Link
                      to="/super-admin"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-purple-300 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <ShieldCheck size={16} className="text-purple-300" />
                      Super Admin
                    </Link>
                  </>
                )}

                <div className="h-px bg-white/10 my-1"></div>
                <button
                  onClick={() => { logout(); setIsUserMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                >
                  <LogOut size={16} className="text-red-400" />
                  Sair
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      <ProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} />
    </nav>
  );
}